import { Dispatch } from 'redux';
import axios, { CancelTokenSource, Method } from 'axios';
import { instanceStoreRecord, InstanceType } from './store';
import {
	Instances,
	ApiMap,
	SubaccountInstances,
	VmnV2ApiInstances,
	V2ApiInstances,
} from '../../constants';
import { OrderingRuleType } from '../../interface/core';
import { IStateType } from '../models/root.interface';
import {
	makeSelectOrderings,
	makeSelectOffset,
	makeSelectPaginate,
	makeSelectRequestParams,
	makeSelectSearchStr,
	makeSelectFilters,
	makeSelectObjectsArray,
	makeSelectSearchTerm,
	makeSelectProperty,
	makeSelectCancelToken,
	makeSelectCount,
	makeSelectGlobalCount,
	makeSelectSort,
	makeSelectLimit,
} from './selectors';
import { convertFilterToUrlParams } from '../../utils/storeUtility';
// import { object } from 'prop-types';
import { isArray, isEmpty, remove } from 'lodash';
import { objectSucceeded, ObjectSucceededType } from '../Object/actions';
import { makeSelectObject } from '../Object/selectors';
import { apiBaseUrl, networkCall } from '../../utils/networkCall';
import { fbCustomHeaders } from '../../pages/Fallback/constants';
// import store from '../store';

export const OBJECTS_REQUESTED = 'objects/OBJECTS_REQUESTED';
export const OBJECTS_SUCCEEDED = 'objects/OBJECTS_SUCCEEDED';
export const OBJECTS_FAILED = 'objects/OBJECTS_FAILED';
export const OFFSET_CHANGED = 'objects/OFFSET_CHANGED';
// export const PAGE_CHANGED = 'objects/PAGE_CHANGED';
export const PAGE_SIZE_CHANGED = 'objects/PAGE_SIZE_CHANGED';
export const SEARCH_STR_CHANGED = 'objects/SEARCH_STR_CHANGED';
export const FILTERS_CHANGED = 'objects/FILTERS_CHANGED';
export const UPDATE_CANCEL_TOKEN = 'objects/UPDATE_CANCEL_TOKEN';
export const CLEAR_FILTERS = 'objects/CLEAR_FILTERS';
export const SORT_CHANGED = 'objects/SORT_CHANGED';
export const REMOVE_OBJECTS = 'object/REMOVE_OBJECTS';

// Create URls
const getUrl = (
	instanceId: Instances,
	defaultInstance: Partial<InstanceType> = {},
	dynamicUrlData: string = '',
	apiName?: string,
) => {
	const instance = instanceStoreRecord(defaultInstance).toJS();
	const {
		offset,
		limit,
		filters,
		orderings,
		sort,
		requestParams,
		pagination,
		searchTerm,
		searchStr,
		page,
	} = instance;

	const apiFn = apiName || ApiMap[instanceId];
	let url = apiFn;
	if (dynamicUrlData) {
		url = `${url}/${dynamicUrlData}`;
	}
	if (typeof apiFn === 'function') {
		url = (apiFn as Function)(requestParams);
	}
	const urlParams = new URLSearchParams();
	// pagination
	if (pagination) {
		// rename limit to appropriate varaible
		let limitValue = limit === undefined ? 10 : limit;
		let offsetValue = parseInt(offset) * limitValue;
		urlParams.append('limit', limit === undefined ? 10 : limit);
		if (page) {
			urlParams.append('page', (offset + 1).toString());
		} else {
			urlParams.append('offset', offsetValue.toString());
		}
	}
	// ordering
	if (orderings?.length) {
		const order = orderings
			.map(({ id, desc }: OrderingRuleType) => `${desc ? '-' : ''}${id}`)
			.join(',');
		urlParams.append('ordering', order);
	}

	// sort
	if (sort?.length) {
		const field = sort.map(({ id }: OrderingRuleType) => `${id}`).join(',');
		const order = sort
			.map(({ id, desc }: OrderingRuleType) => `${desc ? 'desc' : 'asc'}`)
			.join(',');
		urlParams.append('sort', field);
		urlParams.append('order', order);
	}

	//filters
	if (filters) {
		convertFilterToUrlParams(urlParams, filters);
	}
	// search
	if (searchStr) {
		urlParams.append(searchTerm, searchStr);
	}

	return `${url}${url.includes('?') ? '&' : '?'
		}${urlParams.toString()}`;
};

const getBaseUrl = (instanceId: Instances, apiService?: string) => {

	if (instanceId) {
		if (SubaccountInstances.includes(instanceId) || apiService === 'web-api') {
			return apiBaseUrl.webApi;
		}
		if (V2ApiInstances.includes(instanceId)) {
			return apiBaseUrl.baseUrlV2;
		}
		if (VmnV2ApiInstances.includes(instanceId)) {
			return apiBaseUrl.baseUrlV2;
		}
		if (apiService === "chatbotUrl") {
			return apiBaseUrl.chatbotURL;
		}
		if (apiService === "analyticsBeta") {
			return apiBaseUrl.analyticsBeta;
		}
		if (apiService === "wmsApi") {
			return apiBaseUrl.wmsApi;
		}
		if (apiService === "analyticsBeta") {
			return apiBaseUrl.analyticsBeta;
		}
	}
	return apiBaseUrl.baseUrl;
}

const makeDownloadAPIcall = async (
	dispatch: Dispatch<
		ObjectsSucceededType | ObjectsFailedType | UpdateCancelTokenType
	>,
	getState: () => IStateType,
	instanceId: Instances,
	defaultInstance: Partial<InstanceType> = {},
	type: Method = 'get'!,
	reqData: any = undefined!,
	keyForIdentifier?: any,
	// callBack?: (data: any, state: any) => void,
	fileName: string = 'file.xls',
	responseType?: 'blob'
) => {
	const apiKey = localStorage.getItem('KLR_FE_TOKEN');
	const forUserId = localStorage.getItem('for-user-id');
	// check prev cancel token and cancel.
	const prevCancelToken = makeSelectCancelToken(instanceId)(getState());
	if (prevCancelToken) {
		prevCancelToken.cancel();
	}
	// get token and assign
	const cancelToken = axios.CancelToken.source();
	dispatch(updateCancelToken(cancelToken, instanceId));
	try {
		let headers: object = undefined!;
		headers = {
			'api-key': apiKey,
		};
		if (forUserId) {
			headers = {
				'api-key': apiKey,
				'for-user-id': forUserId,
			};
		}
		const config = {
			method: type,
			url: getUrl(instanceId, defaultInstance),
			headers: headers,
			cancelToken: cancelToken.token,
			data: reqData,
			responseType: responseType,
		};
		const response = await axios(config);
		if (response?.data) {
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `${fileName}`); //or any other extension
			document.body.appendChild(link);
			link.click();
		}
	} catch (err) {
		dispatch(objectFailed(instanceId));
	}
};


const makeAPIcall = async (
	dispatch: Dispatch<
		| ObjectsSucceededType
		| ObjectsFailedType
		| UpdateCancelTokenType
		| ObjectSucceededType
	>,
	getState: () => IStateType,
	instanceId: Instances,
	defaultInstance: Partial<InstanceType> = {},
	type: Method = 'get'!,
	reqData: any = undefined!,
	keyForIdentifier?: any,
	callBack?: any,
	includeId: boolean = true,
	includeHeader: boolean = true,
	dynamicUrlData: string = '',
	apiName?: string,
	operation?: string,
	refreshAfterPost?: boolean, // pass undefined if not applicable
	apiService?: string,
) => {
	// check prev cancel token and cancel.
	const prevCancelToken = makeSelectCancelToken(instanceId)(getState());
	if (prevCancelToken) {
		prevCancelToken.cancel();
	}
	// get token and assign
	const cancelToken = axios.CancelToken.source();
	const customHeaderFb = instanceId?.includes("Fallback")? fbCustomHeaders : undefined;
	dispatch(updateCancelToken(cancelToken, instanceId));

	networkCall(type, getUrl(
		instanceId,
		defaultInstance,
		dynamicUrlData,
		apiName
	), getBaseUrl(instanceId, apiService), includeId, includeHeader, reqData, undefined, { cancelToken: cancelToken.token }, true, customHeaderFb)
		.then((response: any) => {
			if (type === 'POST') {
				if (response?.data) {
					let res = response.data;
					if (isEmpty(res.error)) {
						let newData: string | any[] = [];
						const prevData = makeSelectObjectsArray(instanceId)(
							getState()
						);
						const total = makeSelectCount(instanceId)(getState());
						const globalCount = makeSelectGlobalCount(instanceId)(
							getState()
						);
						const responseData = res?.data;

						if (instanceId.includes('ChatbotList')) {
							console.log('');
						} else {
							if (isArray(responseData) && !refreshAfterPost) {
								if (prevData?.length) {
									let alreadyAvailable = false;
									responseData.forEach((ele: any) => {
										prevData.forEach((element: any) => {
											if (ele.id === element.id) {
												alreadyAvailable = true;
											}
										});
										if (alreadyAvailable) {
											remove(prevData, { id: ele.id });
										}
										newData = [ele, ...newData, ...prevData];
										alreadyAvailable = false;
									});
								} else {
									responseData.forEach((element: any) => {
										newData = [element, ...newData];
									});
								}
							} else if (
								!isEmpty(responseData) &&
								!refreshAfterPost
							) {
								newData = [responseData, ...prevData];
							}

							const newGlobalCount = globalCount
								? globalCount + 1
								: 1;

							dispatch(
								objectsSucceeded(
									instanceId,
									newData,
									total + 1,
									newGlobalCount
								)
							);
						}

						if (callBack) {
							callBack(res, true);
						}
					} else {
						if (callBack) {
							callBack(res, false);
						}
					}
				}
			} else if (type === 'PUT') {
				if (response?.data) {
					let res = response.data;
					if (isEmpty(res.error)) {
						let DISPATCH_OBJECTS = true;
						let newData: string | any[] = [];
						const prevData = makeSelectObjectsArray(instanceId)(
							getState()
						);
						const total = makeSelectCount(instanceId)(getState());
						const globalCount = makeSelectGlobalCount(instanceId)(
							getState()
						);

						let filteredEntry;
						if (
							keyForIdentifier !== undefined &&
							keyForIdentifier !== 'id'
						) {
							filteredEntry = prevData.filter(
								(ele: any) =>
									ele[keyForIdentifier] ===
									reqData[keyForIdentifier]
							);
						} else {
							filteredEntry = prevData.filter(
								(ele: any) => ele.id === reqData.id
							);
						}
						if (instanceId.includes('ChatbotList')) {
							newData = prevData.map((item: any) => {
								if (item.bot_name === reqData.name) {
									item.lock = reqData.lock;
									if (Object.keys(res.data).length) {
										return res.data;
									}
								}
								return item;
							});
							dispatch(
								objectsSucceeded(instanceId, newData, total, total)
							);
						} else if (instanceId.includes('Chatbot')) {
							const pData = makeSelectObject(instanceId)(getState());
							DISPATCH_OBJECTS = false;
							let appendData = res.data;
							appendData.intents = pData.intents;
							newData = appendData;
						} else if (instanceId.includes('UserGroup')) {
							DISPATCH_OBJECTS = false;
							newData = res.data;
						} else if (instanceId.includes('SipACL')) {
							DISPATCH_OBJECTS = false;
							newData = res.data;
						} else {
							if (filteredEntry?.length) {
								for (let [key] of Object.entries(
									filteredEntry[0]
								)) {
									for (let [k, v] of Object.entries(reqData)) {
										if (k === key) {
											filteredEntry[0][key] = v;
										}
									}
								}
							}
							// Fetch response if available and replace values
							if (!isEmpty(res?.data)) {
								for (let [key] of Object.entries(
									filteredEntry[0]
								)) {
									for (let [k, v] of Object.entries(res?.data)) {
										if (k === key) {
											filteredEntry[0][key] = v;
										}
									}
								}
							}
							let objToBeDeleted: any = {};
							if (
								keyForIdentifier !== undefined &&
								keyForIdentifier !== 'id'
							) {
								const id =
									res?.data[keyForIdentifier] ||
									reqData[keyForIdentifier];
								objToBeDeleted[keyForIdentifier] = id;
								remove(prevData, objToBeDeleted);
							} else {
								const id = res?.data?.id || reqData?.id;
								remove(prevData, { id: id });
							}
							newData = [filteredEntry[0], ...prevData];
						}
						if (DISPATCH_OBJECTS) {
							dispatch(
								objectsSucceeded(
									instanceId,
									newData,
									total,
									globalCount
								)
							);
						} else {
							dispatch(objectSucceeded(instanceId, newData));
						}
						if (callBack) {
							callBack(res, true);
						}
					} else {
						if (callBack) {
							callBack(res, false);
						}
					}
				}
			} else if (type === 'DELETE') {
				if (response?.data) {
					let res = response.data;
					if (isEmpty(res.error)) {
						let DISPATCH_OBJECTS = true;

						let newData: string | any[] = [];
						const prevData = makeSelectObjectsArray(instanceId)(
							getState()
						);
						const total = makeSelectCount(instanceId)(getState());
						const globalCount = makeSelectGlobalCount(instanceId)(
							getState()
						);
						let newGlobalCount = 0;
						let newTotal = 0;

						if (instanceId.includes('ChatbotList')) {
							newData = prevData.filter(
								(item: any) => item.bot_name !== reqData.name
							);
							newGlobalCount = globalCount - 1;
							newTotal = total - 1;
						} else if (instanceId.includes('Chatbot')) {
							DISPATCH_OBJECTS = false;
							newData = res.data;
						} else {
							let objToBeDeleted: any = {};
							if (reqData && isArray(reqData[keyForIdentifier])) {
								reqData[keyForIdentifier].forEach(
									(element: any) => {
										objToBeDeleted[keyForIdentifier] = element;
										remove(prevData, objToBeDeleted);
									}
								);
								newData = [...prevData];
							} else {
								if (operation === 'disable') {
									let filteredEntry = prevData.filter(
										(ele: any) => ele.id === reqData.id
									);

									for (let [key] of Object.entries(
										filteredEntry[0]
									)) {
										for (let [k] of Object.entries(reqData)) {
											if (
												(k === 'disable' ||
													k === 'enable') &&
												key === 'status'
											) {
												if (k === 'disable') {
													filteredEntry[0][key] = '0';
												} else if (k === 'enable') {
													filteredEntry[0][key] = '1';
												}
											}
										}
									}
									objToBeDeleted[keyForIdentifier] =
										reqData.keyForIdentifier;
									remove(prevData, objToBeDeleted);
									newData = [filteredEntry[0], ...prevData];
								} else if (dynamicUrlData) {
									objToBeDeleted[
										keyForIdentifier
									] = dynamicUrlData;
									remove(prevData, objToBeDeleted);
									newData = [...prevData];
								} else {
									objToBeDeleted[keyForIdentifier] =
										reqData[keyForIdentifier];
									remove(prevData, objToBeDeleted);
									newData = [...prevData];
								}
							}
							newGlobalCount =
								operation === 'disable'
									? globalCount
									: globalCount - 1;
							newTotal = operation === 'disable' ? total : total - 1;
						}

						if (DISPATCH_OBJECTS) {
							dispatch(
								objectsSucceeded(
									instanceId,
									newData,
									newTotal,
									newGlobalCount
								)
							);
						} else {
							dispatch(objectSucceeded(instanceId, newData));
						}

						if (callBack) {
							callBack(res, true);
						}
					} else {
						if (callBack) {
							callBack(res, false);
						}
					}
				}
			} else {
				const { data = [], limit, total } =
					instanceId === 'ChatbotReportsListing'
						? response?.data?.data
						: response.data;
				let newData = [];
				const prevData = makeSelectObjectsArray(instanceId)(getState());
				const globalCount = makeSelectGlobalCount(instanceId)(getState());
				if (Array.isArray(data)) {
					newData = [...prevData, ...data];
				} else {
					newData = [
						...prevData,
						...data[defaultInstance.property || ''],
					];
				}
				const dataTotal = limit === total ? newData.length + 1 : total;
				const newGlobalCount = globalCount ? globalCount : dataTotal;

				dispatch(
					objectsSucceeded(instanceId, newData, dataTotal, newGlobalCount)
				);
				if (callBack) {
					callBack(response.data, true);
				}
			}
		})
		.catch((err: any) => {
			if (type === 'get') {
				dispatch(objectFailed(instanceId));
			}
			if (callBack) {
				callBack(err?.response, false);
			}
		})
};

export type UpdateCancelTokenType = {
	type: typeof UPDATE_CANCEL_TOKEN;
	instanceId: Instances;
	cancelToken: CancelTokenSource;
};

export const updateCancelToken = (
	cancelToken: CancelTokenSource,
	instanceId: Instances
): UpdateCancelTokenType => ({
	type: UPDATE_CANCEL_TOKEN,
	instanceId,
	cancelToken,
});

export type RequestObjectsType = {
	type: typeof OBJECTS_REQUESTED;
	instanceId: Instances;
	filters?: { [k: string]: any };
	defaultInstance?: Partial<InstanceType>;
};

export const requestObjects = (
	instanceId: Instances,
	filters?: { [k: string]: any },
	defaultInstance?: Partial<InstanceType>,
	includeId: boolean = true,
	includeHeader: boolean = true,
	dynamicUrlData: string = '',
	apiName?: string,
	callBack?: (res: any, success: boolean) => void,
	apiService?: string
) => {
	return async (
		dispatch: Dispatch<
			| RequestObjectsType
			| ObjectsSucceededType
			| ObjectsFailedType
			| UpdateCancelTokenType
			| ObjectSucceededType
		>,
		getState: () => IStateType
	) => {
		dispatch({
			type: OBJECTS_REQUESTED,
			instanceId,
			filters,
			defaultInstance,
		});
		await makeAPIcall(
			dispatch,
			getState,
			instanceId,
			{
				...defaultInstance,
				filters,
			},
			'get',
			undefined,
			undefined,
			callBack,
			includeId,
			includeHeader,
			dynamicUrlData,
			apiName,
			undefined,
			undefined,
			apiService
		);
	};
};

export const updateObjects = (
	instanceId: Instances,
	type: Method,
	reqData: any,
	filters?: { [k: string]: any },
	defaultInstance?: Partial<InstanceType>,
	keyForIdentifier: any = 'id'!,
	operation?: string,
	fileName?: string,
	callBack?: any,
	dynamicUrlData: string = '',
	apiName?: string,
	refreshAfterPost?: boolean, // pass undefined if not applicable
	apiService?: string,
	includeId: boolean = true,
	includeHeader: boolean = true,
) => {
	return async (
		dispatch: Dispatch<
			| RequestObjectsType
			| ObjectsSucceededType
			| ObjectsFailedType
			| UpdateCancelTokenType
			| ObjectSucceededType
		>,
		getState: () => IStateType
	) => {
		if (operation === 'DOWNLOAD') {
			await makeDownloadAPIcall(
				dispatch,
				getState,
				instanceId,
				{
					...defaultInstance,
					filters,
				},
				type,
				reqData,
				keyForIdentifier,
				fileName
			);
		} else {
			await makeAPIcall(
				dispatch,
				getState,
				instanceId,
				{
					...defaultInstance,
					filters,
				},
				type,
				reqData,
				keyForIdentifier,
				callBack,
				includeId,
				includeHeader,
				dynamicUrlData,
				apiName,
				operation,
				refreshAfterPost,
				apiService,
			);
		}
	};
};
export type ObjectsSucceededType = {
	type: typeof OBJECTS_SUCCEEDED;
	instanceId: string;
	objects: any[];
	count: number;
	globalCount: number;
	merge: boolean;
};

export const objectsSucceeded = (
	instanceId: string,
	objects: any,
	count: number,
	globalCount: number,
	merge = false
): ObjectsSucceededType => ({
	type: OBJECTS_SUCCEEDED,
	instanceId,
	objects,
	count,
	globalCount,
	merge,
});

export type ObjectsFailedType = {
	type: typeof OBJECTS_FAILED;
	instanceId: string;
};

export const objectFailed = (instanceId: string): ObjectsFailedType => ({
	type: OBJECTS_FAILED,
	instanceId,
});

export type ChangeOffsetType = {
	type: typeof OFFSET_CHANGED;
	instanceId: Instances;
	offset: number;
};

export const changeOffset = (instanceId: Instances, showPages = false, apiService?: string, dynamicUrlData?: string) => {
	return (
		dispatch: Dispatch<
			ChangeOffsetType | ObjectsSucceededType | ObjectsFailedType | UpdateCancelTokenType | ObjectSucceededType
		>,
		getState: () => IStateType
	) => {
		const state = getState();
		const offset = makeSelectOffset(instanceId)(state) + 1;
		const limit = makeSelectLimit(instanceId)(state);
		const count = makeSelectCount(instanceId)(state);
		const offsetValue = parseInt(offset) * limit


		dispatch({
			type: OFFSET_CHANGED,
			instanceId,
			offset,
		});

		const defaultInstance: Partial<InstanceType> = {
			filters: makeSelectFilters(instanceId)(state),
			offset,
			searchStr: makeSelectSearchStr(instanceId)(state),
			orderings: makeSelectOrderings(instanceId)(state),
			sort: makeSelectSort(instanceId)(state),
			requestParams: makeSelectRequestParams(instanceId)(state),
			pagination: makeSelectPaginate(instanceId)(state),
			searchTerm: makeSelectSearchTerm(instanceId)(state),
			property: makeSelectProperty(instanceId)(state),
			page: showPages,
			limit: makeSelectLimit(instanceId)(state)

		};
		if (offsetValue <= count) {
			makeAPIcall(
				dispatch,
				getState,
				instanceId,
				defaultInstance,
				'get',
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				dynamicUrlData,
				undefined,
				undefined,
				undefined,
				apiService
			);

		}
	};
};


export type ChangeSearchStringType = {
	type: typeof SEARCH_STR_CHANGED;
	instanceId: Instances;
	searchStr: string;
};

export const changeSearchStr = (
	instanceId: Instances,
	searchStr: string,
	dynamicUrlData?: string,
	showPages = false,
	apiService?: string
) => {
	return (
		dispatch: Dispatch<
			| ChangeSearchStringType
			| ObjectsSucceededType
			| ObjectsFailedType
			| UpdateCancelTokenType
			| ObjectSucceededType
		>,
		getState: () => IStateType
	) => {
		dispatch({
			type: SEARCH_STR_CHANGED,
			instanceId,
			searchStr,
			dynamicUrlData,
		});

		// enable once API's are ready.
		const state = getState();
		const defaultInstance: Partial<InstanceType> = {
			searchStr,
			filters: makeSelectFilters(instanceId)(state),
			offset: makeSelectOffset(instanceId)(state),
			orderings: makeSelectOrderings(instanceId)(state),
			sort: makeSelectSort(instanceId)(state),
			requestParams: makeSelectRequestParams(instanceId)(state),
			pagination: makeSelectPaginate(instanceId)(state),
			searchTerm: makeSelectSearchTerm(instanceId)(state),
			property: makeSelectProperty(instanceId)(state),
			page: showPages,
		};
		makeAPIcall(
			dispatch,
			getState,
			instanceId,
			defaultInstance,
			undefined,
			undefined,
			undefined,
			undefined,
			true,
			true,
			dynamicUrlData,
			undefined,
			undefined,
			undefined,
			apiService
		);
	};
};

export type ChangeSortType = {
	type: typeof SORT_CHANGED;
	instanceId: Instances;
	sort: OrderingRuleType[];
};

export const changeSort = (
	instanceId: Instances,
	sorting: OrderingRuleType[]
) => {
	return (
		dispatch: Dispatch<
			| ChangeSortType
			| ObjectsSucceededType
			| ObjectsFailedType
			| UpdateCancelTokenType
			| ObjectSucceededType
		>,
		getState: () => IStateType
	) => {
		// enable once API's are ready.
		const state = getState();
		dispatch({
			type: SORT_CHANGED,
			instanceId,
			sort: [...sorting],
		});

		const defaultInstance: Partial<InstanceType> = {
			sort: [...sorting],
			filters: makeSelectFilters(instanceId)(state),
			orderings: makeSelectOrderings(instanceId)(state),
			offset: makeSelectOffset(instanceId)(state),
			searchStr: makeSelectSearchStr(instanceId)(state),
			requestParams: makeSelectRequestParams(instanceId)(state),
			pagination: makeSelectPaginate(instanceId)(state),
			searchTerm: makeSelectSearchTerm(instanceId)(state),
			property: makeSelectProperty(instanceId)(state),
		};
		makeAPIcall(dispatch, getState, instanceId, defaultInstance);
	};
};
export interface IChangeFilters<T> {
	type: typeof FILTERS_CHANGED;
	instanceId: Instances;
	filters: { [k: string]: T };
}

export const changeFilters = <T>(
	instanceId: Instances,
	filters: { [k: string]: T },
	showPages = false,
	apiService?: string
) => {
	return (
		dispatch: Dispatch<
			| IChangeFilters<T>
			| ObjectsSucceededType
			| ObjectsFailedType
			| UpdateCancelTokenType
			| ObjectSucceededType
		>,
		getState: () => IStateType
	) => {
		dispatch({
			type: FILTERS_CHANGED,
			instanceId,
			filters,
		});

		// enable once API's are ready.
		const state = getState();
		const defaultInstance: Partial<InstanceType> = {
			filters,
			offset: makeSelectOffset(instanceId)(state),
			searchStr: makeSelectSearchStr(instanceId)(state),
			orderings: makeSelectOrderings(instanceId)(state),
			sort: makeSelectSort(instanceId)(state),
			requestParams: makeSelectRequestParams(instanceId)(state),
			pagination: makeSelectPaginate(instanceId)(state),
			searchTerm: makeSelectSearchTerm(instanceId)(state),
			property: makeSelectProperty(instanceId)(state),
			page: showPages,
		};
		makeAPIcall(
			dispatch,
			getState,
			instanceId,
			{ ...defaultInstance, filters, },
			'get',
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			apiService
		);
	};
};

export interface IClearFilters<T> {
	type: typeof CLEAR_FILTERS;
	instanceId: Instances;
	searchStr: string;
	filters: { [k: string]: T };
}

export const clearFilters = <T>(
	instanceId: Instances,
	searchStr: string,
	filters: { [k: string]: T },
	callBack?: any
) => {
	return (
		dispatch: Dispatch<
			| IClearFilters<T>
			| ObjectsSucceededType
			| ObjectsFailedType
			| UpdateCancelTokenType
			| ObjectSucceededType
		>,
		getState: () => IStateType
	) => {
		dispatch({
			type: CLEAR_FILTERS,
			instanceId,
			searchStr,
			filters,
		});

		// enable once API's are ready.
		const state = getState();
		const defaultInstance: Partial<InstanceType> = {
			searchStr,
			filters: makeSelectFilters(instanceId)(state),
			offset: makeSelectOffset(instanceId)(state),
			orderings: makeSelectOrderings(instanceId)(state),
			sort: makeSelectSort(instanceId)(state),
			requestParams: makeSelectRequestParams(instanceId)(state),
			pagination: makeSelectPaginate(instanceId)(state),
			searchTerm: makeSelectSearchTerm(instanceId)(state),
			property: makeSelectProperty(instanceId)(state),
		};
		makeAPIcall(
			dispatch,
			getState,
			instanceId,
			{
				...defaultInstance,
				filters,
			},
			'get',
			{},
			'id',
			callBack
		);
	};
};

export type RemoveObjectsType = {
	type: typeof REMOVE_OBJECTS;
	instanceId: string;
};

export const removeObjects = (instanceId: string): RemoveObjectsType => ({
	type: REMOVE_OBJECTS,
	instanceId,
});

export type Action =
	| RequestObjectsType
	| ObjectsSucceededType
	| ObjectsFailedType
	| ChangeOffsetType
	| ChangeSearchStringType
	| IChangeFilters<unknown>
	| UpdateCancelTokenType
	| IClearFilters<unknown>
	| ChangeSortType
	| RemoveObjectsType;